import { queryOptions } from "@tanstack/react-query";
import type {
  RunbookRecord,
  IncidentsServicePort,
  IncidentThreadDto,
  IncidentThreadState,
  RunbooksServicePort,
} from "@bitsentry-ce/components/services";
import { trpcClient } from "../trpc/client";
import { queryClient } from "./client";

// Keep dashboard server state in memory, scoped to the signed-in user. Editor
// drafts remain local to the editor; they must never become executable cache data.
export const dashboardKeys = {
  runbooks: (scope: string) => ["dashboard", scope, "runbooks"] as const,
  summaries: (scope: string) =>
    [...dashboardKeys.runbooks(scope), "summaries"] as const,
  runbook: (scope: string, id: string) =>
    [...dashboardKeys.runbooks(scope), "detail", id] as const,
  incidents: (scope: string) => ["dashboard", scope, "incidents"] as const,
  incidentDetail: (scope: string, id: string) =>
    [...dashboardKeys.incidents(scope), "detail", id] as const,
  integrationConnections: (scope: string) =>
    ["dashboard", scope, "integration-connections"] as const,
  executions: (scope: string) => ["dashboard", scope, "executions"] as const,
  executionList: (scope: string, runbookId?: string) =>
    [
      ...dashboardKeys.executions(scope),
      "list",
      { limit: 100, runbookId },
    ] as const,
};

export type RunbookSummary = Awaited<
  ReturnType<typeof trpcClient.runbooksRouter.listRunbookSummaries.query>
>[number];
export type IncidentList = Awaited<
  ReturnType<IncidentsServicePort["listThreads"]>
>;
export type ExecutionList = Awaited<
  ReturnType<RunbooksServicePort["listExecutions"]>
>;

export const runbookSummariesOptions = (scope: string) =>
  queryOptions({
    queryKey: dashboardKeys.summaries(scope),
    queryFn: ({ signal }) =>
      trpcClient.runbooksRouter.listRunbookSummaries.query(undefined, {
        signal,
      }),
    staleTime: 60_000,
  });

export const runbookOptions = (scope: string, id: string) =>
  queryOptions({
    queryKey: dashboardKeys.runbook(scope, id),
    queryFn: ({ signal }): Promise<RunbookRecord | null> =>
      trpcClient.runbooksRouter.getRunbook.query({ id }, { signal }),
    staleTime: 60_000,
    enabled: id.length > 0,
  });

export const incidentListOptions = (scope: string) =>
  queryOptions({
    queryKey: dashboardKeys.incidents(scope),
    queryFn: async ({ signal }): Promise<IncidentList> =>
      mergeFetchedIncidents(
        scope,
        await trpcClient.incidentsRouter.listThreads.query(
          { includeArchived: true, limit: 100 },
          { signal },
        ),
      ),
    staleTime: 30_000,
  });

export const integrationConnectionsOptions = (scope: string) =>
  queryOptions({
    queryKey: dashboardKeys.integrationConnections(scope),
    queryFn: ({ signal }) =>
      trpcClient.incidentsRouter.listIntegrationConnections.query(undefined, {
        signal,
      }),
    staleTime: 30_000,
  });

export const incidentDetailOptions = (scope: string, threadId: string) =>
  queryOptions({
    queryKey: dashboardKeys.incidentDetail(scope, threadId),
    queryFn: ({ signal }) =>
      trpcClient.incidentsRouter.getThreadDetail.query(
        { threadId },
        { signal },
      ),
    enabled: threadId.length > 0,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

function isTerminalIncidentState(state: IncidentThreadState): boolean {
  return state === "completed" || state === "failed" || state === "cancelled";
}

// A refetch (e.g. window focus) can race a still-streaming thread: the
// server snapshot lags behind the locally cached "running" state until the
// run reaches a terminal state, so keep the cached fields until then.
function mergeFetchedIncidents(
  scope: string,
  response: IncidentList,
): IncidentList {
  const cached = queryClient.getQueryData<IncidentList>(
    dashboardKeys.incidents(scope),
  );
  if (!cached) return response;
  const byId = new Map(cached.threads.map((thread) => [thread.id, thread]));
  const threads = response.threads.map((thread) => {
    const current = byId.get(thread.id);
    if (
      current?.state === "running" &&
      !isTerminalIncidentState(thread.state)
    ) {
      return {
        ...thread,
        title: current.title,
        state: current.state,
        lastMessagePreview: current.lastMessagePreview,
      };
    }
    return thread;
  });
  return { ...response, threads };
}

export function toRunbookSummary(runbook: RunbookRecord): RunbookSummary {
  return {
    id: runbook.id,
    title: runbook.title,
    description: runbook.description,
    updatedAt: runbook.updatedAt,
    actionCount: runbook.actions.length,
  };
}

export async function cacheSavedRunbook(
  scope: string,
  runbook: RunbookRecord,
): Promise<void> {
  // Stop an older list/detail response from overwriting the successful mutation.
  await Promise.all([
    queryClient.cancelQueries({ queryKey: dashboardKeys.summaries(scope) }),
    queryClient.cancelQueries({
      queryKey: dashboardKeys.runbook(scope, runbook.id),
    }),
  ]);
  const current = queryClient.getQueryData<RunbookRecord | null>(
    dashboardKeys.runbook(scope, runbook.id),
  );
  if (current === null) return;
  if (
    current &&
    (current.revisionNumber > runbook.revisionNumber ||
      (current.revisionNumber === runbook.revisionNumber &&
        current.updatedAt >= runbook.updatedAt))
  )
    return;
  queryClient.setQueryData(dashboardKeys.runbook(scope, runbook.id), runbook);
  queryClient.setQueryData<RunbookSummary[]>(
    dashboardKeys.summaries(scope),
    (previous) =>
      previous === undefined
        ? undefined
        : [
            toRunbookSummary(runbook),
            ...previous.filter((item) => item.id !== runbook.id),
          ],
  );
  void queryClient.invalidateQueries({
    queryKey: dashboardKeys.summaries(scope),
  });
}

export async function cacheIncidentThread(
  scope: string,
  thread: IncidentThreadDto,
  created = false,
): Promise<void> {
  await queryClient.cancelQueries({ queryKey: dashboardKeys.incidents(scope) });
  queryClient.setQueryData<IncidentList>(
    dashboardKeys.incidents(scope),
    (previous) => {
      if (!previous) return undefined;
      const exists = previous.threads.some((item) => item.id === thread.id);
      const threads = exists
        ? previous.threads.map((item) =>
            item.id === thread.id ? thread : item,
          )
        : [thread, ...previous.threads];
      return { threads, total: previous.total + (created && !exists ? 1 : 0) };
    },
  );
  if (created)
    void queryClient.invalidateQueries({
      queryKey: dashboardKeys.incidents(scope),
    });
}

export async function cacheIncidentArchive(
  scope: string,
  id: string,
  archived: boolean,
): Promise<void> {
  await queryClient.cancelQueries({ queryKey: dashboardKeys.incidents(scope) });
  const archivedAt = archived ? new Date().toISOString() : null;
  queryClient.setQueryData<IncidentList>(
    dashboardKeys.incidents(scope),
    (previous) =>
      previous === undefined
        ? undefined
        : {
            ...previous,
            threads: previous.threads.map((thread) =>
              thread.id === id ? { ...thread, archivedAt } : thread,
            ),
          },
  );
  // The active conversation also keeps a local detail/draft state.
  window.dispatchEvent(
    new CustomEvent("bitsentry:incidents-updated", {
      detail: {
        action: archived ? "archive" : "unarchive",
        incidentId: id,
        archivedAt,
      },
    }),
  );
  void queryClient.invalidateQueries({
    queryKey: dashboardKeys.incidents(scope),
  });
}

export async function saveRunbookMutation<T extends RunbookRecord | null>(
  scope: string,
  mutate: () => Promise<T>,
): Promise<T> {
  const runbook = await mutate();
  if (runbook !== null) await cacheSavedRunbook(scope, runbook);
  return runbook;
}

export async function cacheDeletedRunbook(
  scope: string,
  id: string,
): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: dashboardKeys.summaries(scope) }),
    queryClient.cancelQueries({ queryKey: dashboardKeys.runbook(scope, id) }),
  ]);
  queryClient.setQueryData(dashboardKeys.runbook(scope, id), null);
  queryClient.setQueryData<RunbookSummary[]>(
    dashboardKeys.summaries(scope),
    (previous) => previous?.filter((item) => item.id !== id),
  );
  void queryClient.invalidateQueries({
    queryKey: dashboardKeys.summaries(scope),
  });
}
